import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as appSelectors from '@store/app/app.selectors';
import * as authActions from '../auth/auth.actions';
import * as authSelectors from '../auth/auth.selectors';
import * as appActions from './app.actions';
import * as customerSelectors from '@store/customer/customer.selectors';
import * as offersSelectors from '@store/offers/offers.selectors';
import * as paymentsSelectors from '@store/payments/payments.selectors';

import { interval, of } from 'rxjs';
import { EAPP_FLOWS, ETRANSACTION_STATUS } from '../../../core/enums/app.enum';
import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppAPI } from '../../../data/mo-api/app.api';
import { AppFacade } from '../../../facades/app.facade';
import { ErrorFacade } from '../../../facades/error.facade';
import { UtilService } from '../../../services/util.service';
import { AppService } from '../../services/app.service';
import { MapErrorService } from '../../services/error/map-error.service';

import { Location } from '@angular/common';
import { APP_CONSTANTS as APP_CONST, APP_CONSTANTS as CONST } from '@app/app.constants';
import { EERROR_TYPE } from '@app/core/models/error.model';
import { Store } from '@ngrx/store';
import { APP_ROUTES as ROUTES } from '../../../app.routes';
import { IExtraData, IPutRecord } from '../../../core/models/app.model';
import { AuthFacade } from '../../../facades/auth.facade';
import { OnboardingFacade } from '../../../facades/onboarding.facade';
import { EVENTS } from '../../../data/events/events-by-category';
import { CustomerAPI } from '../../../data/mo-api/customer.api';
import { ICustomerValidate } from '../../../core/models/customer.model';
import { ERROR_COMPONENT_CONSTANTS } from '../../services/error/error.constants';
import { EERROR_PAGE_TYPES } from '../../../core/models/error.model';
import { ErrorService } from '../../../core/services/error.service';

@Injectable()
export class AppEffects {
  public FLOWS = EAPP_FLOWS;

  constructor(
    private _store: Store,
    private actions$: Actions,
    private _appFacade: AppFacade,
    private _appService: AppService,
    private _authFacade: AuthFacade,
    private _onboardingFacade: OnboardingFacade,
    private _customerAPI: CustomerAPI,
    private _appAPI: AppAPI,
    private _mapErrorService: MapErrorService,
    private _errorFacade: ErrorFacade,
    private _handleTokenService: HandleTokenService,
    private _utilService: UtilService,
    private _location: Location,
    private _errorService: ErrorService
  ) {}

  public getCurrentRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.getCurrentRoute),
      map(({ currentRoute }) => {
        const currentFlow = APP_CONST.FLOWS_BY_ROUTE[currentRoute];
        return appActions.setCurrentFlow({ currentFlow });
      })
    )
  );

  public stepBack$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.stepBack),
        withLatestFrom(
          this._store.select(appSelectors.selectCurrentFlow),
          this._store.select(appSelectors.selectStep)
        ),
        tap(([_, currentFlow, step]) => {
          const category: string = CONST.EVENT_BY_FLOW[currentFlow];
          const { newCurrentFlow, newStep } = this._appService.setStepBackConfig(currentFlow, step);
          this._appFacade.setNextStepConfig(newStep, newCurrentFlow);
          if (currentFlow === this.FLOWS.ONBOARDING && newStep === 1)
            this._onboardingFacade.requestOnboardingSession(newStep);

          if (currentFlow === this.FLOWS.INSTALLMENTS) this._utilService.navigateTo(ROUTES.SCORE);

          if (currentFlow === this.FLOWS.FQA) this._location.back();
        })
      ),
    { dispatch: false }
  );

  public setNewStep$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.setNewStep),
        tap(({ step }) => {
          this._handleTokenService.setUserStep(step);
        })
      ),
    { dispatch: false }
  );

  public requestLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.requestLogin),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(offersSelectors.selectSelectedOfferId)
      ),
      switchMap(([{ phone, code }, { id }, offerId]) =>
        this._appAPI.requestLogin(phone, code, id, offerId).pipe(
          tap((session) => {
            const maskedSession = {
              ...session,
              token: this._utilService.maskedValue(session.token, 4),
            };
            const data: IPutRecord = EVENTS.REGISTER.OTP.REQUEST;
            const extraData: IExtraData = {
              request: { phone, code, offerId },
              response: maskedSession,
            };
            this._appFacade.sendPutRecord(data, extraData);
          }),
          map((session) => {
            const data: IPutRecord = EVENTS.REGISTER.OTP.EVENT;
            this._appFacade.sendPutRecord(data);
            return appActions.successRequestLogin({ session });
          }),
          catchError((error) => {
            const data: IPutRecord = EVENTS.REGISTER.OTP.REQUEST;
            const extraData: IExtraData = {
              request: { phone, code, offerId },
              response: { error: error?.error ?? error },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(appActions.failureRequestLogin({ error }));
          })
        )
      )
    )
  );

  public failureRequestLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.failureRequestLogin),
      withLatestFrom(this._store.select(authSelectors.selectCountErrorOTP)),
      map(([_, countErrorOTP]) =>
        authActions.updateCountTimmerOTP({ countErrorOTP: countErrorOTP + 1 })
      )
    )
  );

  public successRequestLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.successRequestLogin),
      tap((session) => {
        const data: {} = session;
        this._appFacade.sendPutRecord(data);
      }),
      map((_) => appActions.requestCustomerValidate())
    )
  );

  public requestCustomerValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.requestCustomerValidate),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, { id }]) =>
        this._customerAPI.requestUserScore(id).pipe(
          map((validation: ICustomerValidate) => {
            const data: IPutRecord = EVENTS.KYC.REQUEST;
            const extraData: IExtraData = { request: { id }, response: validation };
            this._appFacade.sendPutRecord(data, extraData);
            return appActions.successRequestCustomerValidate({ validation });
          }),
          catchError((error) => {
            const data: IPutRecord = EVENTS.KYC.REQUEST;
            const extraData: IExtraData = {
              request: { id },
              response: { error: error?.error ?? error },
            };
            this._appFacade.sendPutRecord(data, extraData);
            return of(appActions.failureRequestCustomerValidate({ error }));
          })
        )
      )
    )
  );

  public successRequestCustomerValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.successRequestCustomerValidate),
      withLatestFrom(this._store.select(appSelectors.selectSession)),
      map(([{ validation }, session]) => {
        const { can_aggregator_capacity } = validation;
        const { token, is_customer } = session;
        const data: IPutRecord = EVENTS.REGISTER.OTP.EVENT;
        this._appFacade.sendPutRecord(data);
        const errorData = ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_10];
        const errorAggregatorCapacity = ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_19];
        const { route, step, re_validation, error } = this._appService.handleCustomerSession(
          validation,
          session
        );

        this._handleTokenService.setAccessToken(token);
        this._appFacade.setNewStep(step);
        this._handleTokenService.setUserStep(step);

        if (!!error && !!can_aggregator_capacity) {
          this._appFacade.killPurchaseWithoutRedirect(
            'has-payment-capacity',
            is_customer,
            errorData
          );
        }

        if (!!error && !can_aggregator_capacity) {
          this._appFacade.killPurchaseWithoutRedirect(
            'can-aggregator-capacity',
            is_customer,
            errorAggregatorCapacity
          );
        }

        this._utilService.navigateTo(route);

        if (is_customer) {
          const dataFingerPrint = this._utilService.getFingerPrintData();
          this._appFacade.setDataFingerPint(dataFingerPrint);
        }

        return !!re_validation
          ? appActions.requestCustomerReValidate({ route, step })
          : appActions.updateCountTimmerOTP({ route, step });
      })
    )
  );

  public failureRequestCustomerValidate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.failureRequestCustomerValidate),
        tap(({ error }) => {
          const message = this._mapErrorService.mapError(error);
          this._errorFacade.handleError(message);
        })
      ),
    { dispatch: false }
  );

  public requestCustomerReValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.requestCustomerReValidate),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([{ route, step }, { id }]) =>
        this._customerAPI.requestCustomerValidation(id).pipe(
          map((_) => {
            return appActions.successRequestReCustomerValidate({ route, step });
          }),
          catchError((error) => of(appActions.failureRequestReCustomerValidate({ error })))
        )
      )
    )
  );

  public updateCountTimmerOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.updateCountTimmerOTP),
      map(({ route, step }) => {
        this._appFacade.setNewStep(step);
        this._handleTokenService.setUserStep(step);
        this._utilService.navigateTo(route);
        return authActions.updateCountTimmerOTP({ countErrorOTP: 0 });
      })
    )
  );

  public successRequestReCustomerValidate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.successRequestReCustomerValidate),
      map(({ route, step }) => {
        this._appFacade.setNewStep(step);
        this._handleTokenService.setUserStep(step);
        this._utilService.navigateTo(route);
        return authActions.updateCountTimmerOTP({ countErrorOTP: 0 });
      })
    )
  );

  public closeSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.closeSession),
        tap((_) => {
          this._appFacade.killPurchaseWithoutRedirect();
        })
      ),
    { dispatch: false }
  );

  public killPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.killPurchase),
      switchMap((_) =>
        this._appAPI.killPurchase().pipe(
          map((_) => {
            const event = EVENTS.APP.KILL_PURCHASE;
            this._appFacade.sendPutRecord(event);
            return appActions.successKillPurchase();
          }),
          catchError((error) => of(appActions.failureKillPurchase({ error })))
        )
      )
    )
  );

  public successKillPurchase$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.successKillPurchase),
        withLatestFrom(this._store.select(appSelectors.selectTransactions)),
        switchMap(([_, { id }]) => {
          const transactionId: string = this._handleTokenService.getTransactionId().toString();
          return this._appAPI.getTransactions(transactionId).pipe(
            tap((transactions) => {
              const data: IPutRecord = EVENTS.LOAN.REQUEST;
              const extraData: IExtraData = { response: transactions };
              this._appFacade.sendPutRecord(data, extraData);
            }),
            delay(500),
            map(({ redirect_url }) => {
              this._handleTokenService.removeAccessToken();
              this._handleTokenService.cancelTransactionId();
              this._handleTokenService.removeUserStep();
              this._handleTokenService.removeTransactionToken();
              this._authFacade.removeAuthCustomer();
              this._utilService.redirectToUrl(redirect_url);
            })
          );
        })
      ),
    { dispatch: false }
  );

  public failureKillPurchase$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.failureKillPurchase),
        tap((_) => {
          const route: string = ROUTES.ERROR;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public redirectLoop$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.redirectLoop),
        withLatestFrom(
          this._store.select(appSelectors.selectTransactions),
          this._store.select(appSelectors.selectCurrentFlow),
          this._store.select(customerSelectors.selectRedirectUrl)
        ),
        delay(14000),
        tap(([_, transaction, currentFlow, redirectUrl]) => {
          const url: string =
            currentFlow === EAPP_FLOWS.PAYMENT ? redirectUrl : transaction?.redirect_url;
          this._handleTokenService.removeAccessToken();
          this._handleTokenService.cancelTransactionId();
          this._handleTokenService.removeUserStep();
          this._authFacade.removeAuthCustomer();
          return !!url ? this._utilService.redirectToUrl(url) : false;
        })
      ),
    { dispatch: false }
  );

  public sendPutRecord$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.sendPutRecord),
        withLatestFrom(
          this._store.select(appSelectors.selectTransactions),
          this._store.select(customerSelectors.selectCustomer),
          this._store.select(appSelectors.selectCurrentFlow),
          this._store.select(authSelectors.selectPhone),
          this._store.select(authSelectors.selectResend),
          this._store.select(customerSelectors.selectPreLoan),
          this._store.select(paymentsSelectors.selectCreditCard),
          this._store.select(paymentsSelectors.selectPaymentParams),
          this._store.select(paymentsSelectors.selectPaymentResponse)
        ),
        tap(([{ data, extraData }, transaction, customer, currentFlow]) => {
          const { email, customerId } = customer || {};
          const { id, aggregator_id, aggregator_reference } = transaction || {};
          const is_customer = this._authFacade.getAuthCustomer()?.is_customer;

          const customer_id =
            is_customer && currentFlow !== this.FLOWS.AUTH && currentFlow !== this.FLOWS.ONBOARDING
              ? customerId
              : null;

          const extra_data = {
            merchant_id: aggregator_id,
            merchant_reference: aggregator_reference,
            merchant_transaction_id: id,
            current_flow: currentFlow,
            is_customer: is_customer,
            ...extraData,
          };

          this._appFacade.sendGoogleMetrics({
            ...data,
            customer_id,
            extra_data,
          });
          this._appFacade.requestPutRecord({
            ...data,
            customer_id,
            extra_data,
          });
        })
      ),
    { dispatch: false }
  );

  public listenTransactionStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.getTransactionsWithOutId),
        withLatestFrom(this._store.select(appSelectors.selectSession)),
        tap(([_, session]) => {
          return interval(CONST.TIME_SCORE_LOOP)
            .pipe(
              mergeMap(() => {
                const transactionId: string = this._handleTokenService.getTransactionId();
                return this._appAPI.getTransactions(transactionId);
              }),
              tap((transactions) => {
                this._appFacade.setTransactions(transactions);
                if (
                  transactions.status === ETRANSACTION_STATUS.PENDING &&
                  transactions?.others?.payment_error_code
                ) {
                  const errorCode: string = transactions?.others?.payment_error_code;
                  const { is_customer } = session;

                  if (!this._errorService.existSpecialCodes(errorCode)) {
                    return this._errorFacade.handleError(
                      errorCode,
                      EERROR_TYPE.COMPONENT,
                      is_customer
                    );
                  }

                  return this._appFacade.killPurchaseWithoutRedirect(errorCode);
                }
                if (
                  transactions.status === ETRANSACTION_STATUS.EXPIRED ||
                  transactions.status === ETRANSACTION_STATUS.CANCELED
                ) {
                  const { is_customer } = session;
                  const data: IPutRecord = EVENTS.LOAN.REQUEST;
                  const extraData: IExtraData = { response: transactions };
                  this._appFacade.sendPutRecord(data, extraData);
                  this._handleTokenService.cleanAndRemoveAllData();
                  this._appFacade.setRedirectUrl(
                    transactions?.redirect_url,
                    '',
                    is_customer,
                    ERROR_COMPONENT_CONSTANTS[EERROR_PAGE_TYPES.ERROR_12]
                  );
                  return;
                }

                if (transactions.status === ETRANSACTION_STATUS.PROCESSED) {
                  const data: IPutRecord = EVENTS.LOAN.REQUEST;
                  const extraData: IExtraData = { response: transactions };
                  this._appFacade.sendPutRecord(data, extraData);
                  this._appFacade.setRedirectUrlSuccessFlow(transactions?.redirect_url);
                  this._handleTokenService.cleanAndRemoveAllData();
                }
              }),
              takeWhile(
                ({ status, others }) =>
                  status === ETRANSACTION_STATUS.PENDING &&
                  (others?.payment_error_code === '' || others?.payment_error_code === null)
              ),
              catchError((_) => of(this._appFacade.killPurchase()))
            )
            .subscribe();
        })
      ),
    { dispatch: false }
  );

  public setDataFingerPrint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.setDataFingerPrint),
      switchMap(({ data }) =>
        this._appAPI.setFingerPrintData(data).pipe(map((_) => appActions.successDataFingerPrint()))
      )
    )
  );

  public killPurchaseWithoutRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.killPurchaseWithoutRedirect),
      switchMap(({ error, is_customer, componentData }) => {
        const transactionId = this._handleTokenService.getTransactionId();
        return this._appAPI.getTransactions(transactionId).pipe(
          map((transaction) => {
            const { redirect_url } = transaction;
            return !!redirect_url
              ? appActions.successKillPurchaseWithoutRedirect({ error, is_customer, componentData })
              : appActions.killTransaction({ error, componentData });
          })
        );
      })
    )
  );

  public killTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.killTransaction),
      withLatestFrom(this._store.select(appSelectors.selectSession)),
      switchMap(([{ error, componentData }, session]) =>
        this._appAPI.killPurchase().pipe(
          map((_) => {
            const { is_customer } = session;
            return appActions.successKillPurchaseWithoutRedirect({
              error,
              is_customer,
              componentData,
            });
          }),
          catchError((_) => of(appActions.failureKillPurchaseWithoutRedirect()))
        )
      )
    )
  );

  public successKillPurchaseWithoutRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.successKillPurchaseWithoutRedirect),
        switchMap(({ error, is_customer, componentData }) => {
          const transactionId: string = this._handleTokenService.getTransactionId().toString();
          return this._appAPI.getTransactions(transactionId).pipe(
            map((transaction) => {
              const data: IPutRecord = EVENTS.LOAN.REQUEST;
              const extraData: IExtraData = { response: transaction };
              this._appFacade.sendPutRecord(data, extraData);
              this._handleTokenService.cleanAndRemoveAllData();
              this._appFacade.setRedirectUrl(
                transaction.redirect_url,
                error,
                is_customer,
                componentData
              );
            })
          );
        })
      ),
    { dispatch: false }
  );

  public setRedirectUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.setRedirectUrl),
        tap(({ redirectUrl, error, is_customer, componentData }) => {
          return !!error
            ? this._errorFacade.handleError(
                error,
                EERROR_TYPE.COMPONENT,
                is_customer,
                () => {},
                componentData
              )
            : false;
        })
      ),
    { dispatch: false }
  );

  public cleanAndReditectUrl$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(appActions.cleanAndReditectUrl),
        tap(({ redirectUrl }) => {
          this._handleTokenService.cleanAndRemoveAllData();
          this._utilService.redirectToUrl(redirectUrl);
        })
      ),
    { dispatch: false }
  );
}
